import React, { useState } from 'react';
import { ArrowLeftIcon, XIcon, PlusIcon, TagIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { PaymentMethodSelector } from '../components/PaymentMethodSelector';
import { useBookingCart } from '../contexts/BookingCartContext';
export function BookingCartScreen() {
  const navigate = useNavigate();
  const [paymentMethod, setPaymentMethod] = useState('credits');
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [promoMessage, setPromoMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const {
    cartItems,
    removeFromCart,
    confirmBookings,
    appliedPromoCode,
    promoDiscount,
    applyPromoCode,
    removePromoCode
  } = useBookingCart();
  const subtotalUC = cartItems.reduce((sum, item) => sum + item.priceUC, 0);
  const subtotal = cartItems.reduce((sum, item) => sum + item.price, 0);
  const loyaltyDiscount = paymentMethod === 'credits' ? subtotalUC * 0.05 : 0;
  // Apply promo discount
  const totalAfterPromo = paymentMethod === 'credits' ? Math.max(0, subtotalUC - promoDiscount) : subtotal;
  // Apply loyalty discount after promo
  const totalUC = Math.max(0, totalAfterPromo - loyaltyDiscount);
  const total = subtotal - promoDiscount * (subtotal / subtotalUC); // Proportional discount for cash
  const handleApplyPromo = () => {
    const result = applyPromoCode(promoCodeInput);
    setPromoMessage({
      type: result.success ? 'success' : 'error',
      text: result.message
    });
    if (result.success) {
      setPromoCodeInput('');
      setTimeout(() => setPromoMessage(null), 3000);
    } else {
      setTimeout(() => setPromoMessage(null), 3000);
    }
  };
  const handleRemovePromo = () => {
    removePromoCode();
    setPromoMessage(null);
  };
  const handleConfirmBooking = () => {
    const paymentMethodLabel = paymentMethod === 'credits' ? 'UniPlay Credits' : 'Visa •••• 1234';
    confirmBookings(paymentMethodLabel);
    navigate('/booking/processing');
  };
  return <div className="h-screen w-full flex flex-col bg-[#f8f9fa]" data-id="element-229">
      {/* Header */}
      <div className="bg-white px-4 py-4 flex items-center justify-between shadow-sm" data-id="element-230">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-full" data-id="element-231">
          <ArrowLeftIcon className="w-6 h-6 text-[#1f5070]" data-id="element-232" />
        </button>
        <div className="flex-1 flex justify-center" data-id="element-233">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/fYmeFB35HhojatJVz8XpFb/PickleballBruneiLogoC.webp" alt="Pickleball Brunei" className="h-6 object-contain" data-id="element-234" />
        </div>
        <div className="w-10" data-id="element-235"></div>
      </div>

      <div className="flex-1 overflow-auto px-4 py-6 space-y-6" data-id="element-236">
        {/* Cart Items */}
        <div data-id="element-237">
          <div className="flex items-center justify-between mb-4" data-id="element-238">
            <h2 className="text-xl font-bold text-[#1f5070]" data-id="element-239">Your Bookings</h2>
            <button onClick={() => navigate('/calendar')} className="flex items-center gap-1 text-sm text-[#3dc7e3] font-bold active:scale-95 transition-transform" data-id="element-240">
              <PlusIcon className="w-4 h-4" data-id="element-241" />
              Add More
            </button>
          </div>

          {cartItems.length === 0 ? <div className="bg-white rounded-xl shadow-md p-8 text-center" data-id="element-242">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-243">
                <PlusIcon className="w-8 h-8 text-gray-400" data-id="element-244" />
              </div>
              <h3 className="text-lg font-bold text-[#1f5070] mb-2" data-id="element-245">
                No Bookings Yet
              </h3>
              <p className="text-sm text-[#666666] mb-4" data-id="element-246">
                Add courts to your booking to get started
              </p>
              <Button variant="primary" onClick={() => navigate('/home')} data-id="element-247">
                Browse Venues
              </Button>
            </div> : <div className="space-y-4" data-id="element-248">
              {/* Group items by venue */}
              {Object.entries(cartItems.reduce((groups, item) => {
            if (!groups[item.venueId]) {
              groups[item.venueId] = [];
            }
            groups[item.venueId].push(item);
            return groups;
          }, {} as Record<string, typeof cartItems>)).map(([venueId, venueItems]) => <div key={venueId} data-id="element-249">
                  {/* Venue Header */}
                  <div className="flex items-center justify-between mb-3" data-id="element-250">
                    <div data-id="element-251">
                      <h3 className="text-base font-bold text-[#1f5070]" data-id="element-252">
                        {venueItems[0].venueName}
                      </h3>
                      <p className="text-xs text-[#666666]" data-id="element-253">
                        {venueItems[0].venueLocation}
                      </p>
                    </div>
                    <span className="text-xs font-bold text-[#3dc7e3] bg-[#3dc7e3]/10 px-2.5 py-1 rounded-full" data-id="element-254">
                      {venueItems.length}{' '}
                      {venueItems.length === 1 ? 'court' : 'courts'}
                    </span>
                  </div>

                  {/* Venue's Court Items */}
                  <div className="space-y-3 mb-4" data-id="element-255">
                    {venueItems.map(item => <button key={item.id} onClick={() => navigate(`/venue/${item.venueId}/court/${item.courtId}`)} className="w-full bg-white rounded-xl shadow-md p-4 flex items-center gap-4 active:scale-98 transition-transform text-left" data-id="element-256">
                        <img src={item.image} alt={item.courtName} className="w-20 h-28 rounded-lg object-cover flex-shrink-0" data-id="element-257" />
                        <div className="flex-1 min-w-0" data-id="element-258">
                          <h3 className="text-base font-bold text-[#1f5070] mb-1" data-id="element-259">
                            {item.courtName}
                          </h3>
                          <p className="text-sm text-[#666666] mb-1" data-id="element-260">
                            {item.date} • {item.time}
                          </p>
                          <p className="text-sm font-bold text-[#3dc7e3]" data-id="element-261">
                            {item.priceUC} UC or ${item.price}
                          </p>
                        </div>
                        <button onClick={e => {
                  e.stopPropagation();
                  removeFromCart(item.id);
                }} className="p-2 hover:bg-gray-100 rounded-full flex-shrink-0" data-id="element-262">
                          <XIcon className="w-5 h-5 text-gray-400" data-id="element-263" />
                        </button>
                      </button>)}
                  </div>
                </div>)}
            </div>}
        </div>

        {cartItems.length > 0 && <>
            {/* Promo Code Section */}
            <div data-id="element-264">
              <h3 className="text-lg font-bold text-[#1f5070] mb-3" data-id="element-265">
                Promo Code
              </h3>

              {!appliedPromoCode ? <div className="bg-white rounded-xl shadow-md p-4" data-id="element-266">
                  <div className="flex gap-2" data-id="element-267">
                    <div className="flex-1 relative" data-id="element-268">
                      <input type="text" value={promoCodeInput} onChange={e => setPromoCodeInput(e.target.value.toUpperCase())} placeholder="Enter promo code" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#3dc7e3] focus:outline-none font-mono text-sm" onKeyPress={e => e.key === 'Enter' && handleApplyPromo()} data-id="element-269" />
                      <TagIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-270" />
                    </div>
                    <Button variant="primary" onClick={handleApplyPromo} disabled={!promoCodeInput.trim()} data-id="element-271">
                      Apply
                    </Button>
                  </div>

                  {promoMessage && <div className={`mt-3 p-2 rounded-lg text-sm ${promoMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-id="element-272">
                      {promoMessage.text}
                    </div>}

                  <button onClick={() => navigate('/promotion')} className="mt-3 text-sm text-[#3dc7e3] font-bold" data-id="element-273">
                    View available promotions →
                  </button>
                </div> : <div className="bg-gradient-to-r from-[#c7f61e]/20 to-[#3dc7e3]/20 rounded-xl shadow-md p-4 border-2 border-[#c7f61e]" data-id="element-274">
                  <div className="flex items-center justify-between" data-id="element-275">
                    <div className="flex items-center gap-3" data-id="element-276">
                      <div className="w-10 h-10 bg-[#c7f61e] rounded-full flex items-center justify-center" data-id="element-277">
                        <TagIcon className="w-5 h-5 text-white" data-id="element-278" />
                      </div>
                      <div data-id="element-279">
                        <p className="text-sm text-[#666666]" data-id="element-280">Promo Applied</p>
                        <p className="text-lg font-bold text-[#1f5070] font-mono" data-id="element-281">
                          {appliedPromoCode}
                        </p>
                      </div>
                    </div>
                    <button onClick={handleRemovePromo} className="p-2 hover:bg-white/50 rounded-full transition-colors" data-id="element-282">
                      <XIcon className="w-5 h-5 text-gray-600" data-id="element-283" />
                    </button>
                  </div>
                </div>}
            </div>

            {/* Payment Method */}
            <div data-id="element-284">
              <h3 className="text-lg font-bold text-[#1f5070] mb-4" data-id="element-285">
                Payment Method
              </h3>
              <PaymentMethodSelector selectedId={paymentMethod} onSelect={setPaymentMethod} data-id="element-286" />
            </div>

            {/* Price Breakdown */}
            <div className="bg-white rounded-xl shadow-md p-4" data-id="element-287">
              <h3 className="text-lg font-bold text-[#1f5070] mb-4" data-id="element-288">Summary</h3>

              <div className="space-y-2 mb-4" data-id="element-289">
                <div className="flex justify-between text-[#666666]" data-id="element-290">
                  <span data-id="element-291">Subtotal</span>
                  <span data-id="element-292">
                    {paymentMethod === 'credits' ? `${subtotalUC} UC` : `$${subtotal.toFixed(2)}`}
                  </span>
                </div>

                {appliedPromoCode && promoDiscount > 0 && <div className="flex justify-between text-[#c7f61e] font-bold" data-id="element-293">
                    <span data-id="element-294">Promo ({appliedPromoCode})</span>
                    <span data-id="element-295">
                      -
                      {paymentMethod === 'credits' ? `${promoDiscount} UC` : `$${(promoDiscount * (subtotal / subtotalUC)).toFixed(2)}`}
                    </span>
                  </div>}

                {paymentMethod === 'credits' && loyaltyDiscount > 0 && <div className="flex justify-between text-[#43A047]" data-id="element-296">
                    <span data-id="element-297">Loyalty Discount (5%)</span>
                    <span data-id="element-298">-{Math.round(loyaltyDiscount)} UC</span>
                  </div>}
              </div>

              <div className="border-t border-gray-200 pt-4" data-id="element-299">
                <div className="flex justify-between items-center" data-id="element-300">
                  <span className="text-lg font-bold text-[#1f5070]" data-id="element-301">
                    Total
                  </span>
                  <div className="text-right" data-id="element-302">
                    <div className="text-2xl font-bold text-[#1f5070]" data-id="element-303">
                      {paymentMethod === 'credits' ? `${Math.round(totalUC)} UC` : `$${total.toFixed(2)}`}
                    </div>
                    {paymentMethod === 'credits' && <div className="text-sm text-[#666666]" data-id="element-304">
                        ≈ ${total.toFixed(2)}
                      </div>}
                  </div>
                </div>
              </div>
            </div>
          </>}
      </div>

      {/* Bottom Button */}
      {cartItems.length > 0 && <div className="bg-white border-t border-gray-200 p-4" data-id="element-305">
          <Button variant="primary" fullWidth onClick={handleConfirmBooking} data-id="element-306">
            Confirm Booking
          </Button>
        </div>}
    </div>;
}